import { useMemo, useRef, useState } from "react";
import { useSpatial } from "./SpatialContext";
import { isHavenIsland } from "../lib/hotspotPopupContent";
import { dispatchUnityBridgeEvent } from "../lib/unityEvents";
import { confirmDialog } from "../lib/dialog";
import {
  DISTRICT_COLORS,
  MAP_RULES,
  displayLayouts,
  districtBorder,
  getIslandModel,
  islandNameHalfWidth,
  shapesGap,
  islandPolygon,
} from "../lib/worldLayout";
import IslandArt from "./map/IslandArt";

const TEAL = "#4DB8B5";
const ISO_COLORS = ["#7BA08A", "#8878A0", "#6890A0", "#A89070", "#6B8FA0", "#8A7A60"];

const BUILDING_COLS = 3;
const BUILDING_STEP_X = 62;
const BUILDING_STEP_Y = 68;
const MAP_PADDING = 60;

const svgPoint = ([x, z]) => `${x},${-z}`;

function isBuildingUnlocked(building) {
  return building?.is_unlocked !== false;
}

/** Island is locked when every building on it is locked. */
function isIslandUnlocked(island) {
  const buildings = (island.buildings || []).filter((b) => b?.id);
  if (!buildings.length) return true;
  return buildings.some(isBuildingUnlocked);
}

function buildingOffset(index, count) {
  const cols = Math.min(BUILDING_COLS, Math.max(count, 1));
  const rows = Math.ceil(Math.max(count, 1) / cols);
  const col = index % cols;
  const row = Math.floor(index / cols);
  return {
    dx: (col - (cols - 1) / 2) * BUILDING_STEP_X - 22,
    dy: (row - (rows - 1) / 2) * BUILDING_STEP_Y - 38,
  };
}

function mapViewBox(outlines) {
  const bounds = MAP_RULES.mapBounds;
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  const include = (x, z) => {
    minX = Math.min(minX, x); maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
  };
  for (const [x, z] of MAP_RULES.haven?.outline || []) include(x, z);
  for (const outline of outlines.values()) {
    for (const [x, z] of outline) include(x, z);
  }
  minX = Math.max(minX - MAP_PADDING, bounds?.minX ?? -Infinity);
  maxX = Math.min(maxX + MAP_PADDING, bounds?.maxX ?? Infinity);
  minZ = Math.max(minZ - MAP_PADDING, bounds?.minZ ?? -Infinity);
  maxZ = Math.min(maxZ + MAP_PADDING, bounds?.maxZ ?? Infinity);
  return { x: minX, y: -maxZ, width: maxX - minX, height: maxZ - minZ };
}

export default function MapView({ onClose, onEnterRoom, onPositionChange }) {
  const { world, enterRoom, renameNode, deleteNode, currentLocation } = useSpatial();
  const svgRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [selection, setSelection] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [viewMode, setViewMode] = useState("map");

  // const [positions, setPositions] = useState(() => {
  //   const init = {};
  //   for (const d of world?.districts || []) {
  //     for (const isl of d.islands || []) {
  //       for (const b of isl.buildings || []) {
  //         if (b.id && b.map_x != null && b.map_y != null) {
  //           init[b.id] = { x: b.map_x, y: b.map_y };
  //         }
  //       }
  //     }
  //   }
  //   return init;
  // });
  // const dragging = useRef(null);
  // const didDrag = useRef(false);
  // const onPositionChangeRef = useRef(onPositionChange);
  // onPositionChangeRef.current = onPositionChange;
  //
  // const toSVG = (clientX, clientY) => {
  //   const svg = svgRef.current;
  //   if (!svg) return { x: 0, y: 0 };
  //   const rect = svg.getBoundingClientRect();
  //   return {
  //     x: (clientX - rect.left) * (780 / rect.width),
  //     y: (clientY - rect.top) * (520 / rect.height),
  //   };
  // };
  //
  // useEffect(() => {
  //   const onMove = (e) => {
  //     if (!dragging.current) return;
  //     didDrag.current = true;
  //     const mouse = toSVG(e.clientX, e.clientY);
  //     const dx = mouse.x - dragging.current.startMouse.x;
  //     const dy = mouse.y - dragging.current.startMouse.y;
  //     const newPos = { x: dragging.current.startPos.x + dx, y: dragging.current.startPos.y + dy };
  //     const dragId = dragging.current.id;
  //     const dragType = dragging.current.type;
  //     if (dragType === "island") {
  //       setIslandPositions(p => ({ ...p, [dragId]: newPos }));
  //     } else {
  //       setPositions(p => ({ ...p, [dragId]: newPos }));
  //     }
  //     onPositionChangeRef.current?.(dragType, dragId, newPos.x, newPos.y);
  //   };
  //   const onUp = () => { dragging.current = null; };
  //   window.addEventListener("mousemove", onMove);
  //   window.addEventListener("mouseup", onUp);
  //   return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  // }, []);

  const isEmpty = !world?.districts?.length;
  const islands = (world?.districts || []).flatMap(d =>
    (d.islands || []).filter(i => i?.id && !isHavenIsland(i)).map(i => ({ ...i, district: d }))
  );

  const mapDistricts = useMemo(
    () => (world?.districts || [])
      .map(d => ({ ...d, islands: (d.islands || []).filter(i => i?.id && !isHavenIsland(i)) }))
      .filter(d => d.islands.length),
    [world?.districts]
  );
  const layouts = useMemo(() => displayLayouts(mapDistricts), [mapDistricts]);
  const outlines = useMemo(() => {
    const shapes = mapDistricts.map(d => ({
      islands: d.islands.map(i => islandPolygon(i.island_id, layouts.islands.get(i.id)) || []),
      labels: d.islands.map(i => {
        const layout = layouts.islands.get(i.id);
        const baseline = layout.z - (getIslandModel(i.island_id).boundingRadius || 60) * 0.8;
        const half = islandNameHalfWidth(i);
        return [[layout.x - half, baseline - 4], [layout.x + half, baseline - 4], [layout.x + half, baseline + 11], [layout.x - half, baseline + 11]];
      }),
    }));
    return new Map(mapDistricts.map((d, k) => {
      const own = [...shapes[k].islands, ...shapes[k].labels];
      const others = shapes.filter((_, n) => n !== k).flatMap(o => [...o.islands, ...o.labels]);
      const room = others.length ? shapesGap(own, others) : Infinity;
      const offset = Math.max(4, Math.min(20, room / 2 - 5));
      return [d.id, districtBorder(shapes[k].islands, shapes[k].labels, { offset })];
    }));
  }, [mapDistricts, layouts]);
  const viewBox = useMemo(() => mapViewBox(outlines), [outlines]);

  const selectedIsland = selection ? islands.find(i => i.id === selection.islandId) || null : null;
  const selectedBuildingData = selection?.type === "building"
    ? (selectedIsland?.buildings || []).find(b => b?.id === selection.buildingId) || null
    : null;
  const selectedBuilding = selectedBuildingData ? { building: selectedBuildingData, island: selectedIsland } : null;
  const selectedName = selectedBuilding ? selectedBuilding.building.name : selectedIsland?.name;

  const select = (next) => {
    setSelection(next);
    setRenaming(null);
  };

  const handleBuildingClick = (building, island) => {
    // if (didDrag.current) return;
    if (!isIslandUnlocked(island) || !isBuildingUnlocked(building)) return;
    select({ type: "building", islandId: island.id, buildingId: building.id });
  };

  const handleIslandClick = (island) => {
    select({ type: "island", islandId: island.id });
  };

  const saveRename = () => {
    const name = (renaming || "").trim();
    if (!name) return;
    const id = selectedBuilding ? selectedBuilding.building.id : selectedIsland?.id;
    if (id && name !== selectedName) renameNode(id, name);
    setRenaming(null);
  };

  const leaveIfInside = (buildingIds) => {
    const currentBuildingId = currentLocation?.buildingId;
    if (currentBuildingId && buildingIds.includes(currentBuildingId)) {
      dispatchUnityBridgeEvent("BuildingExited", { buildingID: currentBuildingId });
    }
  };

  const handleDelete = async () => {
    if (selectedBuilding) {
      const { building, island } = selectedBuilding;
      const isLastBuilding = (island.buildings || []).filter(b => b?.id).length <= 1;
      const confirmed = await confirmDialog({
        title: isLastBuilding ? "Delete building and island?" : "Delete building?",
        message: isLastBuilding
          ? `"${building.name}" is the last building on "${island.name}", so the island will be deleted too. This can't be undone.`
          : `Delete "${building.name}" and all its rooms? This can't be undone.`,
        confirmLabel: "Delete",
        danger: true,
      });
      if (!confirmed) return;
      deleteNode(isLastBuilding ? island.id : building.id);
      leaveIfInside(isLastBuilding ? (island.buildings || []).map(b => b?.id) : [building.id]);
    } else if (selectedIsland) {
      const confirmed = await confirmDialog({
        title: "Delete island?",
        message: `Delete "${selectedIsland.name}" and everything on it? This can't be undone.`,
        confirmLabel: "Delete",
        danger: true,
      });
      if (!confirmed) return;
      deleteNode(selectedIsland.id);
      leaveIfInside((selectedIsland.buildings || []).map(b => b?.id));
    }
    select(null);
  };

  const handleEnterRoom = (room, floor, building, island) => {
    if (onEnterRoom) {
      onEnterRoom(room, floor, building, island);
      return;
    }
    enterRoom({
      districtId: island.district.id,
      islandId: island.id,
      buildingId: building.id,
      floorId: floor.id,
      roomId: room.id,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-[#F5F0E8] z-[90] flex flex-col font-['DM_Sans']">

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3.5 bg-white/70 border-b border-black/[0.06] flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <span className="text-xl">🌍</span>
          <div>
            <div className="text-[15px] font-bold text-slate-800">{world.name || "My World"}</div>
            <div className="text-[11px] text-slate-400">
              {islands.length} island{islands.length !== 1 ? "s" : ""} · {islands.reduce((a, i) => a + (i.buildings || []).length, 0)} buildings
            </div>
          </div>
        </div>
        <div className="flex gap-1 bg-white/[0.04] rounded-lg p-[3px]">
          {[{ id: "map", label: "🗺 Map" }, { id: "text", label: "≡ Text" }].map(v => (
            <button key={v.id} onClick={() => setViewMode(v.id)}
              className="px-3.5 py-[5px] rounded-md text-xs font-medium cursor-pointer transition-all duration-150"
              style={{
                background: viewMode === v.id ? "rgba(77,184,181,0.2)" : "transparent",
                border: viewMode === v.id ? "1px solid rgba(77,184,181,0.3)" : "1px solid transparent",
                color: viewMode === v.id ? TEAL : "#64748B"
              }}>
              {v.label}
            </button>
          ))}
        </div>
        <button onClick={onClose}
          className="bg-black/[0.06] border border-black/10 rounded-lg px-4 py-2 text-slate-500 text-xs cursor-pointer">
          ✕ Close
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden relative">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <span className="text-5xl">🌍</span>
            <div className="text-base text-slate-500 font-medium">Your world is empty.</div>
            <div className="text-[13px] text-slate-600">Start with a Brain Dump to build your world.</div>
          </div>
        ) : viewMode === "map" ? (
          <>
            <svg ref={svgRef}
              viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
              className="w-full h-[calc(100%-56px)] block"
            >
              <defs>
                <radialGradient id="map_water" cx="50%" cy="50%" r="70%">
                  <stop offset="0%" stopColor="#1E4274" />
                  <stop offset="100%" stopColor="#10284B" />
                </radialGradient>
                <linearGradient id="map_grass" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#74B24A" />
                  <stop offset="100%" stopColor="#4C8C34" />
                </linearGradient>
                <filter id="map_glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <filter id="map_shadow"><feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.6)" /></filter>
              </defs>
              <rect x={viewBox.x - viewBox.width} y={viewBox.y - viewBox.height}
                width={viewBox.width * 3} height={viewBox.height * 3} fill="url(#map_water)" />

              <IslandArt outline={MAP_RULES.haven?.outline || []} seed="haven" />
              <g pointerEvents="none">
                <rect x="-40" y="-11" width="80" height="26" rx="6" fill="rgba(8,22,42,0.78)" />
                <text x="0" y="7" textAnchor="middle" fill="#FFFFFF"
                  fontSize="15" fontWeight="700" fontFamily="'DM Sans',sans-serif" letterSpacing="1.5">
                  HAVEN
                </text>
              </g>

              {mapDistricts.map((district, dIdx) => {
                const outline = outlines.get(district.id) || [];
                if (!outline.length) return null;
                const color = district.color || DISTRICT_COLORS[dIdx % DISTRICT_COLORS.length];
                const topZ = Math.max(...outline.map(p => p[1]));
                const topXs = outline.filter(p => p[1] >= topZ - 1).map(p => p[0]);
                const top = [(Math.min(...topXs) + Math.max(...topXs)) / 2, topZ];
                return (
                  <g key={district.id} pointerEvents="none">
                    <polygon points={outline.map(svgPoint).join(" ")}
                      fill="none" stroke={color} strokeWidth="3" strokeLinejoin="round" filter="url(#map_glow)" />
                    <text x={top[0]} y={-top[1] - 10} textAnchor="middle"
                      fill={color} fontSize="20" fontWeight="700" filter="url(#map_glow)"
                      fontFamily="'DM Sans',sans-serif" letterSpacing="0.4">
                      {district.name}
                    </text>
                  </g>
                );
              })}

              {mapDistricts.flatMap((district, dIdx) => district.islands.map((rawIsland, iIdx) => {
                const island = { ...rawIsland, district };
                const layout = layouts.islands.get(island.id);
                const model = getIslandModel(island.island_id);
                const outline = islandPolygon(island.island_id, layout) || [];
                const scale = (model.boundingRadius || 60) / 130;
                const islandUnlocked = isIslandUnlocked(island);
                const buildings = (island.buildings || []).filter(b => b?.id);

                return (
                  <g key={island.id}>
                    <IslandArt outline={outline} seed={island.id} locked={!islandUnlocked}
                      selected={selectedIsland?.id === island.id} onClick={() => handleIslandClick(island)} />
                    <text x={layout.x} y={-layout.z + (model.boundingRadius || 60) * 0.8} textAnchor="middle" pointerEvents="none"
                      fill={islandUnlocked ? "#FFFFFF" : "rgba(255,255,255,0.72)"}
                      stroke="rgba(5,15,32,0.65)" strokeWidth="3" paintOrder="stroke"
                      fontSize="11" fontWeight="700"
                      fontFamily="'DM Sans',sans-serif" letterSpacing="1.2">
                      {(island.name || "").length > 26 ? `${island.name.toUpperCase().slice(0, 25)}…` : island.name?.toUpperCase()}
                    </text>
                    {!islandUnlocked && (
                      <g transform={`translate(${layout.x - 7}, ${-layout.z - 12})`} opacity="0.7" pointerEvents="none">
                        <rect x="0" y="4" width="14" height="10" rx="1.5" fill="rgba(80,80,80,0.9)" stroke="rgba(160,160,160,0.5)" strokeWidth="0.8" />
                        <path d="M7 7.5 v3.5 M5.5 7.5 h3" stroke="rgba(200,200,200,0.8)" strokeWidth="1.2" strokeLinecap="round" />
                        <path d="M4 4.5 h6 a1.5 1.5 0 0 1 1.5 1.5 v0.5 H2.5 V6 a1.5 1.5 0 0 1 1.5-1.5z"
                          fill="none" stroke="rgba(200,200,200,0.8)" strokeWidth="1" />
                      </g>
                    )}

                    <g transform={`translate(${layout.x},${-layout.z}) scale(${scale.toFixed(4)})`}>
                    {buildings.map((building, bIdx) => {
                      const { dx, dy } = buildingOffset(bIdx, buildings.length);
                      const bx = dx, by = dy;
                      const bw = 44, bh = 28;
                      const floors = (building.floors || []).length;
                      const rooms = (building.floors || []).reduce((n, f) => n + (f.rooms || []).length, 0);
                      const stackH = Math.min(floors * 12 + 16, 56);
                      const buildingUnlocked = islandUnlocked && isBuildingUnlocked(building);
                      const isHov = buildingUnlocked && hovered === building.id;
                      const isSel = buildingUnlocked && selectedBuilding?.building?.id === building.id;
                      const col = buildingUnlocked ? ISO_COLORS[(iIdx + bIdx) % ISO_COLORS.length] : "#6B7280";

                      return (
                        <g key={building.id}
                          onMouseEnter={() => buildingUnlocked && setHovered(building.id)}
                          onMouseLeave={() => setHovered(null)}
                          // onMouseDown={e => {
                          //   e.preventDefault(); e.stopPropagation();
                          //   didDrag.current = false;
                          //   const mouse = toSVG(e.clientX, e.clientY);
                          //   const cur = positions[building.id] || { x: defaultX, y: defaultY };
                          //   dragging.current = { id: building.id, type: "building", startMouse: mouse, startPos: cur };
                          // }}
                          onClick={() => handleBuildingClick(building, island)}
                          style={{ cursor: buildingUnlocked ? "pointer" : "not-allowed" }}
                          filter={buildingUnlocked ? "url(#map_shadow)" : undefined}
                          opacity={buildingUnlocked ? 1 : 0.55}
                        >
                          <polygon points={`${bx},${by} ${bx},${by+bh} ${bx},${by+bh+stackH} ${bx},${by+stackH}`}
                            fill={isHov || isSel ? "rgba(77,184,181,0.6)" : `${col}66`} />
                          <polygon points={`${bx+bw},${by} ${bx+bw},${by+bh} ${bx+bw},${by+bh+stackH} ${bx+bw},${by+stackH}`}
                            fill={isHov || isSel ? "rgba(77,184,181,0.45)" : `${col}44`} />
                          <rect x={bx} y={by+stackH} width={bw} height={bh}
                            fill={isHov || isSel ? "#4DB8B5" : col} rx="1" />
                          <rect x={bx} y={by} width={bw} height={stackH}
                            fill={isHov || isSel ? "rgba(77,184,181,0.8)" : `${col}AA`} rx="1" />
                          {Array.from({ length: Math.min(rooms, 4) }).map((_, wi) => (
                            <rect key={wi} x={bx+5+wi*10} y={by+stackH+8} width="6" height="7" rx="1"
                              fill={isHov || isSel ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.25)"} />
                          ))}
                          {isSel && (
                            <rect x={bx-4} y={by-4} width={bw+8} height={bh+stackH+8} rx="4"
                              fill="none" stroke="#4DB8B5" strokeWidth="2" strokeDasharray="5 3" />
                          )}
                          {rooms > 0 && !isHov && !isSel && buildingUnlocked && (
                            <g>
                              <circle cx={bx+bw-2} cy={by+2} r="9" fill="#EAB308" />
                              <text x={bx+bw-2} y={by+7} textAnchor="middle"
                                fill="#000" fontSize="14" fontFamily="'DM Sans',sans-serif" fontWeight="700">
                                {rooms}
                              </text>
                            </g>
                          )}
                          {!buildingUnlocked && (
                            <text x={bx+bw/2} y={by+bh/2+stackH/2+3} textAnchor="middle"
                              fill="rgba(255,255,255,0.55)" fontSize="9" fontFamily="'DM Sans',sans-serif">
                              🔒
                            </text>
                          )}
                        </g>
                      );
                    })}
                    </g>
                  </g>
                );
              }))}

              {(() => {
                if (!hovered) return null;
                for (const district of mapDistricts) {
                  for (const island of district.islands) {
                    const bIdx = (island.buildings || []).filter(b => b?.id).findIndex(b => b.id === hovered);
                    if (bIdx < 0) continue;
                    const building = island.buildings.filter(b => b?.id)[bIdx];
                    const layout = layouts.islands.get(island.id);
                    const scale = (getIslandModel(island.island_id).boundingRadius || 60) / 130;
                    const { dx, dy } = buildingOffset(bIdx, island.buildings.filter(b => b?.id).length);
                    const cx = layout.x + (dx + 22) * scale;
                    const top = -layout.z + dy * scale - 6;
                    const name = building.name || "";
                    const labelW = name.length * 7 + 12;
                    return (
                      <g pointerEvents="none">
                        <rect x={cx - labelW / 2} y={top - 18} width={labelW} height={18} rx="4" fill="rgba(0,0,0,0.75)" />
                        <text x={cx} y={top - 5} textAnchor="middle" fill="white" fontSize="12"
                          fontFamily="'DM Sans',sans-serif" fontWeight="600">{name}</text>
                      </g>
                    );
                  }
                }
                return null;
              })()}
            </svg>

            {/* Room picker */}
            {selectedIsland && (
              <div className="absolute bottom-[56px] left-1/2 -translate-x-1/2 w-[min(580px,88vw)] rounded-[14px] px-[18px] py-4 shadow-[0_-8px_40px_rgba(0,0,0,0.25)]"
                style={{ background: "rgba(252,249,244,0.98)", border: "1px solid rgba(77,184,181,0.3)" }}>
                <div className={`flex items-center justify-between gap-3 ${selectedBuilding ? "mb-3" : ""}`}>
                  <div className="min-w-0 flex-1">
                    {renaming !== null ? (
                      <input autoFocus value={renaming} maxLength={60}
                        onChange={e => setRenaming(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") saveRename();
                          if (e.key === "Escape") setRenaming(null);
                        }}
                        className="w-full text-[13px] font-bold text-slate-800 px-2 py-1 rounded-md outline-none font-['DM_Sans']"
                        style={{ border: `1px solid ${TEAL}`, background: "white" }} />
                    ) : (
                      <div className="text-[13px] font-bold text-slate-800 truncate">
                        {selectedBuilding ? "🏢" : "🏝"} {selectedName}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-px">
                      {selectedBuilding
                        ? selectedIsland.name
                        : `${selectedIsland.district?.name || ""} · ${(selectedIsland.buildings || []).filter(b => b?.id).length} buildings${isIslandUnlocked(selectedIsland) ? "" : " · locked"}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {renaming !== null ? (
                      <>
                        <button onClick={saveRename} disabled={!renaming.trim()}
                          className="py-[5px] px-3 rounded-lg text-xs font-semibold cursor-pointer border-none disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ background: TEAL, color: "white" }}>Save</button>
                        <button onClick={() => setRenaming(null)}
                          className="py-[5px] px-3 rounded-lg text-xs font-medium cursor-pointer bg-black/[0.05] border-none text-slate-600">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setRenaming(selectedName || "")}
                          className="py-[5px] px-3 rounded-lg text-xs font-medium cursor-pointer bg-black/[0.05] border-none text-slate-700">Rename</button>
                        <button onClick={handleDelete}
                          className="py-[5px] px-3 rounded-lg text-xs font-medium cursor-pointer border-none"
                          style={{ background: "rgba(239,68,68,0.1)", color: "#DC2626" }}>Delete</button>
                      </>
                    )}
                    <button onClick={() => select(null)}
                      className="bg-none border-none text-slate-600 cursor-pointer text-base leading-none ml-1">✕</button>
                  </div>
                </div>
                {selectedBuilding && (
                <div className="flex flex-col gap-2.5 max-h-[200px] overflow-y-auto">
                  {(selectedBuilding.building.floors || []).map(floor => (
                    <div key={floor.id}>
                      <div className="text-[10px] font-bold text-green-500 uppercase tracking-[0.06em] mb-1.5">
                        ▤ {floor.name}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {(floor.rooms || []).map(room => (
                          <button key={room.id}
                            onClick={() => handleEnterRoom(room, floor, selectedBuilding.building, selectedBuilding.island)}
                            className="py-[7px] px-3.5 rounded-lg text-xs font-medium cursor-pointer font-['DM_Sans']"
                            style={{ background: "rgba(77,184,181,0.08)", border: "1px solid rgba(77,184,181,0.2)", color: "#4DB8B5" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "rgba(77,184,181,0.2)"; e.currentTarget.style.borderColor = "#4DB8B5"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "rgba(77,184,181,0.08)"; e.currentTarget.style.borderColor = "rgba(77,184,181,0.2)"; }}>
                            🚪 {room.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}

            {/* Lumen bar */}
            <div className="absolute bottom-0 left-0 right-0 h-[56px] px-6 bg-white/85 border-t border-black/[0.06] flex items-center gap-2.5">
              <div className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[9px] flex-shrink-0"
                style={{ background: "rgba(77,184,181,0.12)", border: "1px solid rgba(77,184,181,0.35)", color: TEAL }}>✦</div>
              <div className="text-[13px]"
                style={{ color: selectedIsland ? "#4DB8B5" : "#94A3B8", fontStyle: selectedIsland ? "normal" : "italic" }}>
                {selectedBuilding
                  ? `${selectedBuilding.building.name} — pick a room to enter`
                  : selectedIsland
                    ? `${selectedIsland.name} — rename or delete it, or click one of its buildings`
                    : "Where do you want to start? Click a building to see its rooms, or an island to rename or delete it. Locked islands are greyed out."
                }
              </div>
            </div>
          </>
        ) : (
          /* Text view */
          <div className="overflow-auto h-full p-6">
            <div className="max-w-[600px] mx-auto">
              {(world.districts || []).map(district => (
                <div key={district.id} className="mb-5">
                  <div className="text-sm font-bold mb-1.5" style={{ color: TEAL }}>🌍 {district.name}</div>
                  {(district.islands || []).filter((i) => !isHavenIsland(i)).map(island => {
                    const islandUnlocked = isIslandUnlocked(island);
                    return (
                    <div key={island.id} className="pl-5 mb-2.5" style={{ opacity: islandUnlocked ? 1 : 0.45 }}>
                      <div className="text-[13px] font-semibold mb-1" style={{ color: islandUnlocked ? "#C084FC" : "#94A3B8" }}>
                        🏝 {island.name}{!islandUnlocked ? " 🔒" : ""}
                      </div>
                      {(island.buildings || []).map(building => {
                        const buildingUnlocked = islandUnlocked && isBuildingUnlocked(building);
                        return (
                        <div key={building.id} className="pl-5 mb-2" style={{ opacity: buildingUnlocked ? 1 : 0.6 }}>
                          <div className="text-xs font-semibold mb-[3px]" style={{ color: buildingUnlocked ? "#3B82F6" : "#94A3B8" }}>
                            🏢 {building.name}{!buildingUnlocked ? " 🔒" : ""}
                          </div>
                          {(building.floors || []).map(floor => (
                            <div key={floor.id} className="pl-5 mb-1">
                              <div className="text-[11px] text-green-500 font-semibold mb-0.5">▤ {floor.name}</div>
                              {(floor.rooms || []).map(room => (
                                <div key={room.id}
                                  onClick={() => buildingUnlocked && handleEnterRoom(room, floor, building, { ...island, district })}
                                  className="pl-5 mb-0.5 text-xs py-[3px] px-2 pl-7 rounded"
                                  style={{
                                    color: buildingUnlocked ? "#94A3B8" : "#CBD5E1",
                                    cursor: buildingUnlocked ? "pointer" : "not-allowed",
                                  }}
                                  onMouseEnter={e => {
                                    if (!buildingUnlocked) return;
                                    e.currentTarget.style.background = "rgba(77,184,181,0.08)";
                                    e.currentTarget.style.color = "#4DB8B5";
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.color = buildingUnlocked ? "#94A3B8" : "#CBD5E1";
                                  }}>
                                  🚪 {room.name}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      );})}
                    </div>
                  );})}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
